import React from 'react';
import { useFormContext } from 'react-hook-form';
import TrackingService from '@/services/tracking';
import { formatTimeCode } from '@/utils/date';
import {
  Text,
  Input,
  Textarea,
  DotBadge,
  RadioButton,
  Box,
} from '@design-system/pc';
import { ItemCode, TimeCode } from '@remodeling/remodeling-manager';
import DateTimeBtn from './DateTimeBtn';
import PictureList from './PictureList';

interface FormContentProps {
  isNameDisabled: boolean;
  isTelDisabled: boolean;
  handlePopupDateTime: () => void;
  items: ItemCode[];
  times: TimeCode[];
  handleChangeTelInput: (value: string) => void;
  handleChangeName: (value: React.FormEvent<HTMLInputElement>) => void;
  handleAddPictureList: (value: File) => void;
  handleRemovePictureFile: (value: number) => void;
  isKindListView: boolean;
  channelType?: string;
}

const FormContent = ({
  isNameDisabled,
  isTelDisabled,
  items,
  isKindListView,
  handlePopupDateTime,
  handleChangeTelInput,
  handleAddPictureList,
  handleChangeName,
  handleRemovePictureFile,
}: FormContentProps) => {
  const {
    setValue,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Box width="570px" {...TrackingService.tagGTM('visit_consult_form')}>
      {/* 이름 */}
      <Box py="20px">
        <Box py="8px">
          <DotBadge
            offset={{
              right: -8,
              top: 0,
            }}
            size="sm"
            zIndex={50}
          >
            <Text variant="subTitle5" color="color_chip.primary">
              이름
            </Text>
          </DotBadge>
        </Box>
        <Box py="4px">
          <Input
            flex="1"
            disabled={isNameDisabled}
            placeholder="이름 입력"
            size="lg"
            {...register('userName', {
              onChange: (e) => handleChangeName(e),
            })}
          />
          {errors.userName && (
            <Text
              variant="caption1"
              color={'color_chip.red.500'}
              marginTop={'8px'}
            >
              {errors.userName.message as string}
            </Text>
          )}
        </Box>
      </Box>

      {/* 휴대폰 번호 */}
      <Box py="20px">
        <Box py="8px">
          <DotBadge
            offset={{
              right: -8,
              top: 0,
            }}
            size="sm"
            zIndex={50}
          >
            <Text variant="subTitle5" color="color_chip.primary">
              휴대폰번호
            </Text>
          </DotBadge>
        </Box>
        <Box py="4px">
          <Input
            placeholder="숫자만 입력"
            size="lg"
            disabled={isTelDisabled}
            onChange={({ target }) => handleChangeTelInput(target.value)}
            value={watch('userTel')}
          />
          {errors.userTel && (
            <Text
              variant="caption1"
              color={'color_chip.red.500'}
              marginTop={'8px'}
            >
              {errors.userTel.message as string}
            </Text>
          )}
        </Box>
      </Box>

      {/* 상담 품목 */}
      {isKindListView && (
        <Box py="20px">
          <Box py="8px">
            <DotBadge
              offset={{
                right: -8,
                top: 0,
              }}
              size="sm"
              zIndex={50}
            >
              <Text variant="subTitle5" color="color_chip.primary">
                상담 품목
              </Text>
            </DotBadge>
          </Box>
          <Box
            py="8px"
            display="flex"
            flexWrap="wrap"
            width="100%"
            style={{ columnGap: '24px', rowGap: '16px' }}
          >
            {items.map((item) => (
              <RadioButton
                key={item.code}
                labelOptions={{
                  text: item.name,
                  textVariant: 'body1',
                }}
                disabled={false}
                checked={watch('itemType') === item.code}
                onClick={() => setValue('itemType', item.code)}
                {...TrackingService.tagGTM(
                  'visit_consult_form_kind_dropdown_item_button',
                  {
                    'option-value': item.code,
                  },
                )}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* 방문 일시 */}
      <Box py="20px">
        <Box py="8px">
          <DotBadge
            offset={{
              right: -8,
              top: 0,
            }}
            size="sm"
            zIndex={50}
          >
            <Text variant="subTitle5" color="color_chip.primary">
              방문 일시
            </Text>
          </DotBadge>
        </Box>
        <Box py="4px">
          <DateTimeBtn
            onClick={handlePopupDateTime}
            value={
              watch('visitDate') !== undefined &&
              watch('visitTimeType') !== undefined
                ? `${watch('visitDate').replaceAll(
                    '-',
                    '.',
                  )} / ${formatTimeCode(watch('visitTimeType'))}`
                : ''
            }
            {...TrackingService.tagGTM(
              'visit_consult_form_open_visit_date_popup_button',
            )}
          />
        </Box>
      </Box>

      {/* 남기고 싶은 말 */}
      <Box py="20px">
        <Box py="8px">
          <Text variant="subTitle5" color="color_chip.primary">
            남기고 싶은 말 (선택)
          </Text>
        </Box>
        <Box py="4px" textAlign="right">
          <Textarea
            minHeight="148px"
            {...register('comment', {
              onChange: ({ target }) =>
                target.value.length < 300
                  ? setValue('comment', target.value)
                  : setValue('comment', target.value.slice(0, 300)),
            })}
            placeholder={
              '상담사에게 전달하고 싶은 말을 남겨주세요. (예시: 좋아하는 색상, 원하는 스타일, 별도의 요청사항 등)'
            }
            {...register('comment')}
          />
          <Text variant="caption1" color="color_chip.gray.500" mt="8px">
            ({watch('comment').length}/300)
          </Text>
        </Box>
      </Box>

      {/* 사진 첨부 */}
      <Box py="20px">
        <Box py="8px">
          <Text variant="subTitle5" color="color_chip.primary">
            사진 첨부 (최대 5장)
          </Text>
        </Box>
        <Box py="4px">
          <PictureList
            handleAddPictureList={handleAddPictureList}
            handleRemovePictureFile={handleRemovePictureFile}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FormContent;
